import { useQuery } from '@tanstack/react-query';
import { progressoService, catalogService } from '../../shared/services';
import { PlayCircle, BookOpen, Award, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

export function HomePage() {
  const { data: progresso, isLoading: loadingProgresso, isError: errorProgresso } = useQuery({
    queryKey: ['progresso-resumo'],
    queryFn: progressoService.getResumo,
    retry: 1,
    staleTime: 30000,
  });

  const { data: cursosData, isLoading: loadingCursos, isError: errorCursos } = useQuery({
    queryKey: ['cursos-home'],
    queryFn: () => catalogService.getCursos({ page: 1 }),
    retry: 1,
    staleTime: 30000,
  });

  if (loadingProgresso) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Mostrar mensagem se backend estiver com problemas
  if (errorProgresso && errorCursos) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Servidor Indisponível</h2>
          <p className="text-gray-600 mb-4">
            A API do backend não está respondendo. Entre em contato com o suporte.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bem-vindo de volta!</h1>
          <p className="text-gray-600 mt-2">Continue sua jornada de aprendizado</p>
        </div>

        {/* Cards de progresso */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cursos em Andamento</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {progresso?.cursosInscritos && progresso?.cursosConcluidos 
                    ? (progresso.cursosInscritos - progresso.cursosConcluidos) 
                    : 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <BookOpen className="text-primary-600" size={24} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cursos Concluídos</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {progresso?.cursosConcluidos || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Award className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aulas Assistidas</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {progresso?.aulasAssistidas || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Continuar assistindo */}
        {progresso?.ultimosCursos && progresso.ultimosCursos.length > 0 && (
          <div className="card mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Continuar assistindo</h2>
            <div className="flex items-center gap-6">
              <div className="relative w-64 h-36 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                {progresso.ultimosCursos[0].thumbnail ? (
                  <img
                    src={progresso.ultimosCursos[0].thumbnail}
                    alt={progresso.ultimosCursos[0].titulo}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PlayCircle size={48} className="text-gray-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <PlayCircle size={48} className="text-white" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600">Progresso: {progresso.ultimosCursos[0].percentualConcluido.toFixed(1)}%</p>
                <h3 className="text-lg font-semibold text-gray-900 mt-1">
                  {progresso.ultimosCursos[0].titulo}
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  {progresso.ultimosCursos[0].aulasConcluidas} de {progresso.ultimosCursos[0].totalAulas} aulas concluídas
                </p>
                <Link
                  to={`/cursos/${progresso.ultimosCursos[0].cursoId}`}
                  className="btn-primary mt-4 inline-block"
                >
                  Continuar curso
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Cursos recomendados */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Cursos Recomendados</h2>
            <Link to="/catalogo" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              Ver todos
            </Link>
          </div>
          
          {loadingCursos ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cursosData && Array.isArray(cursosData) && cursosData.slice(0, 6).map((curso) => (
                <Link
                  key={curso.id}
                  to={`/cursos/${curso.slug}`}
                  className="group"
                >
                  <div className="bg-gray-100 rounded-lg overflow-hidden mb-3 aspect-video">
                    {curso.capaUrl ? (
                      <img
                        src={curso.capaUrl}
                        alt={curso.titulo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen size={48} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {curso.titulo}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{curso.categoria}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {curso.cargaHorariaMin} minutos
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
